import { useEffect, useMemo, useState } from "react";
import { Alert, Box, Select, Grid, Button, Autocomplete, MenuItem } from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers";
import moment from 'moment';
import currency from 'currency.js';

import { getControl } from "../../fields";
import { addContractDays } from "../../functions";
import MoneyInput from "../generic/MoneyInput";
import MySnackbar from "../generic/MySnackbar";
import AnnexesUploader from "../generic/AnnexesUploader";
import SelectUser from "../generic/SelectUser";
import ContractNewsTasksModal from "./ContractNewsTasksModal";

export const NEWS_TYPES = {
  ADDITION_AND_EXTENSION: "ADICIÓN Y PRÓRROGA",
  // AMENDMENT: "OTROSÍ MODIFICATORIO",
  // CLARIFICATION: "OTROSÍ ACLARATORIO",
  SUSPENSION: "SUSPENSIÓN",
  RESUMPTION_AFTER_SUSPENSION: "REINICIO DESPUÉS DE SUSPENSIÓN",
  SUSPENSION_RESUMPTION: "REANUDACIÓN DE SUSPENSIÓN",
  // SUSPENSION_CORRECTION: "CORRECCIÓN DE SUSPENSIÓN",
  TERMINATION: "TERMINACIÓN ANTICIPADA",
  // POLICY_AND_APPROVAL: "POLIZA Y APROBACIÓN",
  RESPONSIBILITIES_CHANGE: "CAMBIOS DE OBLIGACIONES",
  CHANGE_SUPERVISOR: "CAMBIO DE SUPERVISOR",
  ASSIGN_SUPERVISOR: "ASIGNACIÓN DE SUPERVISOR"
};

const controls = {
  type: {
    type: Select,
    options: Object.keys(NEWS_TYPES),
    optionItem: (option) => (
      <MenuItem key={option} value={option}>
        {NEWS_TYPES[option]}
      </MenuItem>
    ),
    label: "Tipo de novedad",
    xs: 12
  },
  startedAt: {
    type: DatePicker,
    required: true,
    label: "Fecha de inicio de novedad",
    xs: 4,
  },
  finishedAt: {
    type: DatePicker,
    label: "Fecha de finalización de novedad",
    xs: 4,
    onValidation: (submission) => !["CHANGE_SUPERVISOR", "TERMINATION", "RESUMPTION_AFTER_SUSPENSION", "RESPONSIBILITIES_CHANGE"].includes(submission.type)
  },
  supervisor: {
    type: SelectUser,
    placeholder: "Nuevo Supervisor",
    xs: 8,
    noedit: true,
    onValidation: (submission) => ["CHANGE_SUPERVISOR", "ASSIGN_SUPERVISOR"].includes(submission.type)
  },
  amount: {
    type: MoneyInput,
    label: "Adición al contrato",
    xs: 4,
    onValidation: (submission) => submission.type === "ADDITION_AND_EXTENSION"
  },
  description: {
    type: Autocomplete, 
    freeSolo: true,
    options: [
      "Viaje",
      "Incapacidad médica",
      "Calamidad doméstica",
      "Licencia de maternidad",
      "Licencia de paternidad",
      "Solicitud del contratista",
      "Nuevas funciones al contrato"
    ],
    label: "Description",
    placeholder: "Incapacidad médica",
    xs: 12
  }
};

// `manualPaymentPlan` = true es el modo de los contratos de PERSONA JURÍDICA:
// su plan de pagos es manual y por hitos, de modo que la reanudación de
// suspensión solo extiende el plazo y NO reparte saldo reprogramando meses. En
// ese modo el formulario no muestra el saldo disponible ni valida contra él.
// Con el default (false) el comportamiento es el del flujo natural, intacto.
// Ver openspec/changes/legal-contract-news/design.md (Decisión 3).
const ContractNewsForm = ({
  contract,
  setNews,
  getContractFieldWithNews,
  manualPaymentPlan = false,
  onSubmit
}) => {
  const [submission, setSubmission] = useState(
    Object.fromEntries(Object.entries(controls).map(([key]) => [key, ""]))
  );
  const [infoSnack, setInfoSnack] = useState({ open: false });
  const [showTasksModal, setShowTasksModal] = useState(false);
  const [tasks, setTasks] = useState([]);
  const [annexes, setAnnexxes] = useState([]);

  // Datos que gobiernan la reanudación de suspensión: el saldo a favor que
  // dejaron las suspensiones y hasta dónde alcanza. Se leen del resolvedor del
  // contrato completo (`contract` aquí es un objeto reducido, sin paymentPlan)
  // para que el formulario valide contra la misma fuente que el cálculo.
  const resumption = useMemo(() => {
    // Con plan manual (jurídicas) no hay saldo que repartir: el calendario
    // mensual que alimenta balance/dailyRate no aplica a un plan por hitos.
    const balance = manualPaymentPlan ? 0 : Number(getContractFieldWithNews?.('balance')) || 0;
    const dailyRate = manualPaymentPlan ? 0 : Number(getContractFieldWithNews?.('dailyRate')) || 0;
    const maxDays = dailyRate > 0 ? Math.floor(balance / dailyRate) : 0;

    return {
      balance,
      dailyRate,
      maxDays,
      contractFinishedAt: contract.finishedAt,
      hasTermination: (contract.news || []).some((news) => news.type === "TERMINATION"),
      maxFinishedAt: submission.startedAt && maxDays > 0
        ? addContractDays(submission.startedAt, maxDays)
        : null
    };
  }, [contract, getContractFieldWithNews, manualPaymentPlan, submission.startedAt]);

  const validationRules = [
    {
      message: "Debe seleccionar un tipo de novedad",
      condition: () => !submission.type
    },
    {
      message: "Debe seleccionar una fecha de inicio de novedad",
      condition: () => !submission.startedAt
    },
    {
      message: "La fecha de finalización de novedad no puede ser anterior a la fecha de inicio de novedad",
      condition: () =>
        submission.finishedAt &&
        submission.startedAt &&
        submission.finishedAt.isBefore(submission.startedAt, 'day')
    },
    {
      message: "Debe seleccionar una fecha de finalización de novedad",
      condition: () =>
        ["ADDITION_AND_EXTENSION"].includes(submission.type)
        && !submission.finishedAt
    },
    {
      message: "La suma de todas las adiciones no puede superar el 50% del valor del contrato",
      condition: () => {
        if (submission.type !== "ADDITION_AND_EXTENSION") return false;

        const contractBudget = Number(contract.contractBudget) || 0;
        const previousAdditions = contract.news
          .filter(news => news.type === "ADDITION_AND_EXTENSION" && news.amount)
          .reduce((sum, news) => sum + Number(news.amount), 0);
        const currentAddition = Number(submission.amount) || 0;

        return (previousAdditions + currentAddition) > (contractBudget * 0.5);
      }
    },
    {
      message: `La fecha de inicio de la novedad debe ser IGUAL a 1 día después de la fecha de finalización del contrato si no se agrega una adición al contrato`,
      condition: () => {
        const oneDayAfterfinishedAt = contract.finishedAt.clone().add(1, 'day');
        return (
          submission.type === "ADDITION_AND_EXTENSION" &&
          !submission.amount &&
          submission.startedAt &&
          !submission.startedAt.isSame(oneDayAfterfinishedAt, 'day')
        );
      }
    },
    {
      message: "El periodo de suspensión del contrato debe estar dentro del periodo de contrato",
      condition: () =>
        ["SUSPENSION"].includes(submission.type)
        && (
          (submission.startedAt && submission.startedAt.isBefore(contract.startedAt, 'day')) ||
          (submission.startedAt && submission.startedAt.isAfter(contract.finishedAt, 'day')) ||
          (submission.finishedAt && submission.finishedAt.isAfter(contract.finishedAt, 'day'))
        )
    },
    {
      message: "El periodo de suspensión no puede solaparse con otra suspensión existente",
      condition: () => {
        if (!["SUSPENSION"].includes(submission.type) || !submission.startedAt || !submission.finishedAt) {
          return false;
        }

        return contract.news.some(news => {
          if (news.type !== "SUSPENSION" || !news.startedAt || !news.finishedAt) return false;
          return (
            (submission.startedAt.isSame(news.finishedAt, 'day') || submission.startedAt.isBefore(news.finishedAt, 'day')) && 
            (submission.finishedAt.isSame(news.startedAt, 'day') || submission.finishedAt.isAfter(news.startedAt, 'day'))
          );
        });
      }
    },
    {
      message: "La fecha de inicio para reinicio debe estar dentro de un periodo de suspensión existente (actual o futuro)",
      condition: () => {
        if (!["RESUMPTION_AFTER_SUSPENSION"].includes(submission.type) || !submission.startedAt) {
          return false;
        }

        // const now = moment();
        const insideAnySuspension = contract.news.some(news => {
          if (news.type !== "SUSPENSION" || !news.startedAt || !news.finishedAt) return false;
          // if (news.finishedAt.isBefore(now, 'day')) return false;

          return (
            (submission.startedAt.isSame(news.startedAt, 'day') || submission.startedAt.isAfter(news.startedAt, 'day')) &&
            (submission.startedAt.isSame(news.finishedAt, 'day') || submission.startedAt.isBefore(news.finishedAt, 'day'))
          );
        });

        return !insideAnySuspension;
      }
    },
    {
      message: "La fecha de terminación anticipada debe ser menor a la fecha de finalización del contrato",
      condition: () => {
        if (!["TERMINATION"].includes(submission.type)) return false;

        let finishedAt = contract.finishedAt;
        const currentNewsIndex = contract.news.length;

        contract.news.slice(0, currentNewsIndex).forEach(news => {
          if (news.type === "ADDITION_AND_EXTENSION" && news.finishedAt) {
            finishedAt = news.finishedAt;
          }
        });

        return submission.startedAt.isSameOrAfter(finishedAt, 'day');
      }
    },
    {
      message: "Debe seleccionar el nuevo supervisor",
      condition: () =>
        ["CHANGE_SUPERVISOR"].includes(submission.type)
        && (!submission.supervisor)
    },
    {
      message: "El contrato no tiene presupuesto a favor, así que no hay saldo que reanudar",
      // Depende del saldo del plan mensual: no aplica con plan manual.
      condition: () =>
        !manualPaymentPlan
        && submission.type === "SUSPENSION_RESUMPTION"
        && resumption.maxDays < 1
    },
    {
      message: "No se puede reanudar un contrato con terminación anticipada",
      condition: () =>
        submission.type === "SUSPENSION_RESUMPTION"
        && resumption.hasTermination
    },
    {
      message: `La reanudación debe empezar después de la fecha de finalización del contrato (${resumption.contractFinishedAt ? moment(resumption.contractFinishedAt).format('DD/MM/YYYY') : '--'})`,
      condition: () =>
        submission.type === "SUSPENSION_RESUMPTION"
        && submission.startedAt
        && resumption.contractFinishedAt
        && !submission.startedAt.isAfter(resumption.contractFinishedAt, 'day')
    },
    {
      message: "Debe seleccionar una fecha de finalización de novedad",
      condition: () =>
        submission.type === "SUSPENSION_RESUMPTION"
        && !submission.finishedAt
    },
    {
      message: `El saldo a favor alcanza para ${resumption.maxDays} día(s): la fecha máxima de finalización es ${resumption.maxFinishedAt ? resumption.maxFinishedAt.format('DD/MM/YYYY') : '--'}`,
      // Tope derivado del saldo del plan mensual: no aplica con plan manual.
      condition: () =>
        !manualPaymentPlan
        && submission.type === "SUSPENSION_RESUMPTION"
        && submission.finishedAt
        && resumption.maxFinishedAt
        && submission.finishedAt.isAfter(resumption.maxFinishedAt, 'day')
    }
  ];

  const handleValidation = () => {
    for (const rule of validationRules) {
      if (rule.condition()) {
        return rule.message;
      }
    }

    return undefined;
  };

  const handleSubmit = () => {
    const message = handleValidation();
    if (message) return setInfoSnack({ open: true, message, severity: "error" });
    const tmpSubmission = { ...submission };

    if (submission.type === "RESPONSIBILITIES_CHANGE" && tasks.length > 0) {
      tmpSubmission.tasks = tasks;
    }

    setNews([...contract.news, { ...tmpSubmission, annexes }]);
    if (onSubmit) onSubmit();
  };

  const handleChange = (key, value) => {
    setSubmission({ ...submission, [key]: value });
  };

  useEffect(() => {
    if (submission.type === "ADDITION_AND_EXTENSION" && !submission.startedAt) {
      setSubmission({ ...submission, startedAt: contract.finishedAt.clone().add(1, 'day') });
    }

    // La reanudación arranca por defecto el día siguiente al fin VIGENTE del
    // contrato, pero el administrador puede moverla a cualquier fecha posterior.
    if (submission.type === "SUSPENSION_RESUMPTION" && !submission.startedAt && resumption.contractFinishedAt) {
      setSubmission({ ...submission, startedAt: moment(resumption.contractFinishedAt).clone().add(1, 'day') });
    }
  }, [submission, contract.finishedAt, resumption.contractFinishedAt]);

  return (
    <>
      <ContractNewsTasksModal
        open={showTasksModal}
        contract={{ ...contract }}
        onClose={(localTasks) => {
          setTasks(localTasks);
          setShowTasksModal(false);
        }}
      />

      <MySnackbar
        info={infoSnack}
        handleClose={() => setInfoSnack({ open: false })}
      />

      <Box component="form" onSubmit={handleSubmit}>
        <Grid container spacing={2}>
          {submission.type === "SUSPENSION_RESUMPTION" && manualPaymentPlan
            && <Grid item xs={12}>
              <Alert severity={resumption.hasTermination ? "warning" : "info"}>
                {resumption.hasTermination
                  ? "El contrato tiene una terminación anticipada, así que no admite reanudación."
                  : <>
                    La reanudación extiende el plazo del contrato. El plan de pagos
                    es manual: ajústelo desde el plan de pagos para repartir el
                    presupuesto entre los hitos.
                  </>}
              </Alert>
            </Grid>
          }

          {submission.type === "SUSPENSION_RESUMPTION" && !manualPaymentPlan
            && <Grid item xs={12}>
              <Alert severity={resumption.maxDays > 0 && !resumption.hasTermination ? "info" : "warning"}>
                {resumption.hasTermination
                  ? "El contrato tiene una terminación anticipada, así que no admite reanudación."
                  : resumption.maxDays < 1
                    ? "El contrato no tiene presupuesto a favor, así que no hay saldo que reanudar."
                    : <>
                      Saldo a favor: <b>{currency(resumption.balance, { precision: 0 }).format()}</b>
                      {" · "}Valor por día: <b>{currency(resumption.dailyRate, { precision: 0 }).format()}</b>
                      {" · "}Alcanza para <b>{resumption.maxDays} día(s)</b>.
                      <br />
                      El contrato termina el <b>{moment(resumption.contractFinishedAt).format('DD/MM/YYYY')}</b>
                      {resumption.maxFinishedAt
                        && <> y, con la fecha de inicio elegida, la reanudación puede llegar hasta el <b>{resumption.maxFinishedAt.format('DD/MM/YYYY')}</b></>}
                      . Los días entre el fin del contrato y el inicio de la reanudación no se pagan.
                    </>}
              </Alert>
            </Grid>
          }

          {Object.keys(controls).map((key) => getControl({
            ...controls[key],
            key,
            handleChange,
            value: submission[key] || "",
            submission
          }))}

          <Grid item xs={12}>
            <AnnexesUploader
              annexes={annexes}
              setAnnexes={setAnnexxes}
            />
          </Grid>

          {submission.type === "RESPONSIBILITIES_CHANGE"
            && <Grid item xs={12}>
              <Button variant="outlined" type="button" onClick={() => setShowTasksModal(true)}>
                Modificar responsabilidades
              </Button>
            </Grid>
          }

          <Grid item xs={12} sx={{ textAlign: "center", mt: 3 }}>
            <Button
              variant="contained"
              type="button"
              onClick={handleSubmit}
            >
              Añadir novedad
            </Button>
          </Grid>
        </Grid>
      </Box>
    </>
  );
};

export default ContractNewsForm;
